/**
 * useShadowSpawnBatches Hook
 *
 * Fetches and manages shadow spawn batches that cascade from block batch requirements.
 * Provides visibility into spawn production needed to support block batches.
 *
 * US-PE-003: Shadow Batch System - Phase 4 React Hooks
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'
import { createDate, toLocalDateString } from '@/utils/dateUtils'

/**
 * Hook to fetch and manage shadow spawn batches
 *
 * @param {Object} params - Query parameters
 * @param {number} [params.weeksAhead=26] - How many weeks to look ahead
 * @param {string|null} [params.strainId] - Optional strain filter
 * @param {boolean} [params.includeReal=false] - Include non-shadow spawn batches
 * @returns {Object} {
 *   shadowSpawnBatches: Array - Shadow spawn batch records,
 *   spawnByWeek: Object - Batches grouped by ready-by week,
 *   loading: boolean,
 *   error: string|null,
 *   refetch: Function
 * }
 */
export function useShadowSpawnBatches({
  weeksAhead = 26,
  strainId = null,
  includeReal = false
} = {}) {
  const [shadowSpawnBatches, setShadowSpawnBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Calculate date range for query
   * Uses inoculation_date (spawn start) rather than demand_week_start
   * to avoid showing shadow batches with past spawn dates
   */
  const dateRange = useMemo(() => {
    const today = createDate()
    const end = createDate()
    end.setDate(end.getDate() + (weeksAhead * 7))

    return {
      todayDate: toLocalDateString(today),
      endDate: toLocalDateString(end)
    }
  }, [weeksAhead])

  /**
   * Fetch shadow spawn batches from database
   */
  const fetchShadowSpawnBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('spawn_batches')
        .select(`
          id,
          code,
          strain_id,
          liquid_culture_id,
          bag_count,
          grain_type,
          bag_weight_kg,
          status,
          inoculation_date,
          expected_ready_date,
          is_shadow,
          target_bags,
          demand_week_start,
          last_recalculated_at,
          created_at,
          updated_at,
          strains!spawn_batches_strain_id_fkey (
            id,
            name,
            code,
            origin
          )
        `)
        // Filter by inoculation_date (spawn start) >= today
        // This excludes shadow batches where production window has passed
        .gte('inoculation_date', dateRange.todayDate)
        .lte('demand_week_start', dateRange.endDate)
        .order('inoculation_date', { ascending: true })

      // Filter by shadow status
      if (!includeReal) {
        query = query.eq('is_shadow', true)
      }

      // Filter by strain
      if (strainId) {
        query = query.eq('strain_id', strainId)
      }

      const { data, error: fetchError } = await query

      if (fetchError) throw fetchError

      // Fetch linked block batches for each spawn batch
      const spawnWithBlocks = await Promise.all(
        (data || []).map(async (spawn) => {
          const { data: linkedBlocks } = await supabase
            .from('production_batches')
            .select('id, batch_code, total_blocks, demand_week_start')
            .eq('required_spawn_batch_id', spawn.id)
            .eq('is_shadow', true)

          return {
            ...spawn,
            linkedBlockBatches: linkedBlocks || []
          }
        })
      )

      setShadowSpawnBatches(spawnWithBlocks)
    } catch (err) {
      console.error('[useShadowSpawnBatches] Error fetching:', err.message || err)
      setError(err.message)
      setShadowSpawnBatches([])
    } finally {
      setLoading(false)
    }
  }, [dateRange, strainId, includeReal])

  /**
   * Group spawn batches by ready-by week
   */
  const spawnByWeek = useMemo(() => {
    const grouped = {}

    shadowSpawnBatches.forEach(spawn => {
      const week = spawn.demand_week_start
      if (!grouped[week]) {
        grouped[week] = []
      }
      grouped[week].push(spawn)
    })

    return grouped
  }, [shadowSpawnBatches])

  /**
   * Get summary statistics
   */
  const summary = useMemo(() => {
    const totalBags = shadowSpawnBatches.reduce(
      (sum, s) => sum + (s.target_bags || s.bag_count || 0), 0
    )
    const totalLinkedBlocks = shadowSpawnBatches.reduce(
      (sum, s) => sum + (s.linkedBlockBatches?.length || 0), 0
    )

    return {
      batchCount: shadowSpawnBatches.length,
      weekCount: Object.keys(spawnByWeek).length,
      totalBags,
      totalLinkedBlocks,
      strainsCount: new Set(shadowSpawnBatches.map(s => s.strain_id)).size
    }
  }, [shadowSpawnBatches, spawnByWeek])

  /**
   * Get spawn batches for a specific week
   *
   * @param {string} weekStart - Week start date (YYYY-MM-DD)
   * @returns {Array} Spawn batches for that week
   */
  const getSpawnForWeek = useCallback((weekStart) => {
    return spawnByWeek[weekStart] || []
  }, [spawnByWeek])

  /**
   * Check if spawn is available for a block batch inoculation week
   *
   * @param {string} inoculationWeek - Block batch inoculation week
   * @param {string} checkStrainId - Strain ID
   * @returns {Object} { available: boolean, spawnBatches: Array, totalBags: number }
   */
  const checkSpawnAvailability = useCallback((inoculationWeek, checkStrainId) => {
    const weekSpawn = shadowSpawnBatches.filter(
      s => s.demand_week_start === inoculationWeek &&
           s.strain_id === checkStrainId
    )

    if (weekSpawn.length === 0) {
      return {
        available: false,
        spawnBatches: [],
        totalBags: 0
      }
    }

    const totalAvailable = weekSpawn.reduce(
      (sum, s) => sum + (s.target_bags || s.bag_count || 0), 0
    )

    return {
      available: totalAvailable > 0,
      spawnBatches: weekSpawn,
      totalBags: totalAvailable
    }
  }, [shadowSpawnBatches])

  // Initial fetch
  useEffect(() => {
    fetchShadowSpawnBatches()
  }, [fetchShadowSpawnBatches])

  // Real-time subscription for spawn batch changes
  useEffect(() => {
    const channel = supabase
      .channel('shadow-spawn-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'spawn_batches',
          filter: 'is_shadow=eq.true'
        },
        () => {
          fetchShadowSpawnBatches()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [fetchShadowSpawnBatches])

  return {
    shadowSpawnBatches,
    spawnByWeek,
    summary,
    loading,
    error,
    getSpawnForWeek,
    checkSpawnAvailability,
    refetch: fetchShadowSpawnBatches
  }
}

export default useShadowSpawnBatches
